// src/views/proprio/ProprioAvisView.jsx — AVIS (Mon bien, refonte lot ⑤,
// spec Dimitri 2026-08-22 §6).
//
//   · EN TÊTE, DEUX BLOCS : la note générale en 44 px avec ses cinq étoiles
//     #d4a24c et « sur N avis » ; à côté LA RÉPARTITION — une ligne par palier
//     de 5 à 1, barre proportionnelle (#7fc4a3 pour 5, #d4a24c pour 4,
//     #b8412b en dessous) et le compte ;
//   · PLUSIEURS LOGEMENTS → un filtre par logement (pastilles) — la note et la
//     répartition SUIVENT le filtre ;
//   · chaque avis : pastille ronde 32 px aux initiales, nom 12.5 gras,
//     « logement · date » 10 px, la note à droite en pastille discrète #f8fafc,
//     le commentaire en italique entre guillemets français ;
//   · PAS de trait vertical coloré à gauche des cartes (demande explicite).
//
// DGCCRF inchangée (ADR-183) : texte ORIGINAL voyageur, TOUS les avis, aucune
// sélection — la note est calculée sur l'ensemble du périmètre affiché.
// LECTURE SEULE, cloisonné serveur (proprio_avis, logIds JWT).

import { useState, useEffect, useCallback, useMemo } from 'react'
import { Star } from 'lucide-react'
import { DS } from '../../lib/ui'
import { fetchProprioAvis, fetchProprioLogements } from '../../lib/proprio-api'
import { SectionTitle, Loading, ErrorBox, EmptyBox, ReadOnlyHint } from './_proprioUi'

const OR = '#d4a24c'

// Couleur d'une barre de palier : 5 → vert, 4 → or, 3 et moins → terracotta.
export function couleurPalier(palier) {
  if (palier >= 5) return '#7fc4a3'
  if (palier >= 4) return '#d4a24c'
  return '#b8412b'
}

// Répartition des notes par palier (arrondi entier 1..5) + note moyenne.
// Calculée sur le PÉRIMÈTRE AFFICHÉ (le filtre logement suit) — les avis sans
// note comptent dans le total mais pas dans la moyenne (aligné serveur).
export function repartitionNotes(avis) {
  const parPalier = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }
  let somme = 0
  let notes = 0
  for (const a of Array.isArray(avis) ? avis : []) {
    const r = Number(a?.rating)
    if (!Number.isFinite(r) || r <= 0) continue
    const p = Math.max(1, Math.min(5, Math.round(r)))
    parPalier[p] += 1
    somme += r
    notes += 1
  }
  return {
    parPalier,
    total: (Array.isArray(avis) ? avis : []).length,
    moyenne: notes > 0 ? Math.round((somme / notes) * 100) / 100 : null,
  }
}

// Initiales du voyageur (« Claire Dubois » → « CD », 1 à 2 lettres).
export function initialesDe(nom) {
  const mots = String(nom || '').trim().split(/\s+/).filter(Boolean)
  if (!mots.length) return '·'
  return mots.slice(0, 2).map((m) => m.charAt(0).toUpperCase()).join('')
}

// « 18 août » (règle des dates : ni année, ni jour de semaine).
const MOIS_MIN = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet',
  'août', 'septembre', 'octobre', 'novembre', 'décembre']
export function dateCourte(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''))
  return m ? `${Number(m[3])} ${MOIS_MIN[Number(m[2]) - 1]}` : ''
}

function fmtNote(n) {
  return n == null ? '—' : Number(n).toFixed(1).replace('.', ',')
}

export default function ProprioAvisView() {
  const [state, setState] = useState({ loading: true, error: null, avis: [], logements: [] })
  const [filtre, setFiltre] = useState('all')

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }))
    try {
      const [avisRes, logRes] = await Promise.all([fetchProprioAvis(), fetchProprioLogements()])
      setState({ loading: false, error: null, avis: avisRes.avis || [], logements: logRes.logements || [] })
    } catch (e) {
      setState({ loading: false, error: e.message, avis: [], logements: [] })
    }
  }, [])

  useEffect(() => { load() }, [load])

  const visibles = useMemo(
    () => (filtre === 'all' ? state.avis : state.avis.filter((a) => a.logement_id === filtre)),
    [state.avis, filtre],
  )
  const rep = useMemo(() => repartitionNotes(visibles), [visibles])
  const nomDe = (lid) => state.logements.find((l) => l.id === lid)?.nom || ''
  const maxPalier = Math.max(...Object.values(rep.parPalier), 1)

  // ⚠️ LA PHRASE SUIT LE PÉRIMÈTRE REGARDÉ (Dimitri 2026-08-22) : elle disait
  // « la note est calculée sur l'ensemble » alors qu'un filtre logement était
  // actif et que la note affichée était celle de ce seul logement.
  // ⚠️ DGCCRF : le filtre change le PÉRIMÈTRE, jamais la SÉLECTION — dans les
  // deux cas on montre TOUS les avis du périmètre, aucun n'est écarté. La
  // phrase doit rester exacte là-dessus.
  const sousTitreAvis = filtre === 'all'
    ? 'Les avis de vos voyageurs — tous, sans sélection (la note est calculée sur l\'ensemble).'
    : `Tous les avis de ${nomDe(filtre) || 'ce logement'} — la note est calculée sur ${rep.total > 1 ? `ces ${rep.total} avis` : 'cet avis'}.`

  return (
    <div>
      <SectionTitle icon={Star} sub={sousTitreAvis}>
        Avis
      </SectionTitle>
      <ReadOnlyHint />

      {state.loading && <Loading />}
      {!state.loading && state.error && <ErrorBox message={state.error} onRetry={load} />}

      {!state.loading && !state.error && (
        <>
          {/* Filtre par logement — la note et la répartition SUIVENT. */}
          {state.logements.length > 1 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
              {[{ id: 'all', nom: 'Tous les logements' }, ...state.logements].map((l) => {
                const on = filtre === l.id
                return (
                  <button key={l.id} type="button" data-testid={`filtre-avis-${l.id}`}
                    onClick={() => setFiltre(l.id)} className="fbz-press"
                    style={{ padding: '6px 13px', borderRadius: 20, border: 'none',
                      background: on ? '#fff' : '#eef2f6',
                      boxShadow: on ? '0 1px 4px rgba(35,50,64,.14)' : 'none',
                      color: on ? '#233240' : '#5c6773', fontSize: 12,
                      fontWeight: on ? 700 : 500, cursor: 'pointer', fontFamily: DS.fontBody }}>
                    {l.nom}
                  </button>
                )
              })}
            </div>
          )}

          {/* EN TÊTE — deux blocs : la note générale + la répartition. */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            <div data-testid="avis-note-generale"
              style={{ flex: '1 1 190px', border: '1px solid #e5eaf0', borderRadius: 12,
                background: '#fff', padding: '16px 18px', display: 'flex',
                flexDirection: 'column', justifyContent: 'center' }}>
              <div style={{ fontFamily: DS.fontDisplay, fontSize: 44, fontWeight: 700,
                color: '#233240', lineHeight: 1 }}>
                {fmtNote(rep.moyenne)}
              </div>
              <div aria-hidden="true" style={{ display: 'flex', gap: 2, margin: '8px 0 4px' }}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} size={16} strokeWidth={1.6} color={OR}
                    fill={rep.moyenne != null && i <= Math.round(rep.moyenne) ? OR : 'none'} />
                ))}
              </div>
              <div style={{ fontSize: 11.5, color: '#5c6773' }}>sur {rep.total} avis</div>
            </div>
            <div data-testid="avis-repartition"
              style={{ flex: '2 1 260px', border: '1px solid #e5eaf0', borderRadius: 12,
                background: '#fff', padding: '13px 16px' }}>
              {[5, 4, 3, 2, 1].map((p) => {
                const n = rep.parPalier[p]
                return (
                  <div key={p} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '3px 0' }}>
                    <span style={{ width: 22, fontSize: 11.5, fontWeight: 700, color: '#3d5166',
                      display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                      {p}<Star size={10} strokeWidth={2} color={OR} fill={OR} />
                    </span>
                    <span style={{ flex: 1, height: 7, borderRadius: 4, background: '#f0f3f6', overflow: 'hidden' }}>
                      <span style={{ display: 'block', height: '100%', borderRadius: 4,
                        width: `${Math.round((n / maxPalier) * 100)}%`,
                        background: n > 0 ? couleurPalier(p) : 'transparent' }} />
                    </span>
                    <span style={{ width: 24, fontSize: 11, color: '#5c6773', textAlign: 'right' }}>{n}</span>
                  </div>
                )
              })}
            </div>
          </div>

          {visibles.length === 0 && <EmptyBox>Aucun avis pour l'instant.</EmptyBox>}

          {/* LES AVIS — pastille aux initiales, pas de trait coloré. */}
          {visibles.map((a) => (
            <div key={a.id} data-testid={`avis-${a.id}`}
              style={{ border: '1px solid #e5eaf0', borderRadius: 12, background: '#fff',
                padding: '12px 14px', marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span aria-hidden="true"
                  style={{ width: 32, height: 32, borderRadius: '50%', background: '#e8f2fa',
                    color: '#3d5166', fontSize: 12, fontWeight: 700, display: 'inline-flex',
                    alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {initialesDe(a.guest_name)}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: '#233240' }}>
                    {a.guest_name || 'Voyageur'}
                  </div>
                  <div style={{ fontSize: 10, color: '#5c6773', marginTop: 1 }}>
                    {[nomDe(a.logement_id), dateCourte(a.review_date)].filter(Boolean).join(' · ')}
                  </div>
                </div>
                {Number(a.rating) > 0 && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                    background: '#f8fafc', border: '1px solid #eef2f6', borderRadius: 8,
                    padding: '4px 8px', fontSize: 12, fontWeight: 700, color: '#233240', flexShrink: 0 }}>
                    <Star size={12} strokeWidth={2} color={OR} fill={OR} />
                    {Number(a.rating).toFixed(1).replace('.', ',').replace(',0', '')}
                  </span>
                )}
              </div>
              {a.review_text && (
                <div style={{ fontSize: 12.5, color: '#3d5166', fontStyle: 'italic',
                  lineHeight: 1.5, marginTop: 8, overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>
                  « {a.review_text} »
                </div>
              )}
            </div>
          ))}
        </>
      )}
    </div>
  )
}
